import { type BaseHTMLAttributes, type CSSProperties, type ReactNode, type Ref, useMemo } from 'react';
import { match, P } from 'ts-pattern';
import { getCurrencyFormatter } from '@interxfi/utilities/currency/formatting';
import { getIntlNumberFormat } from '@interxfi/utilities/intl/number';
import { parseNumber } from '@interxfi/utilities/number/parse';
import { Skeleton } from '../../loading/skeleton';
import { BaseTypography, type TypographyProps } from '../../typography';

export function defaultNumberFormatEmptyRenderer(parts: Intl.NumberFormatPart[]) {
  const currencySymbol = parts.find((part) => part.type === 'currency')?.value;

  return [currencySymbol, '--'].filter(Boolean).join('');
}

export function loadingNumberFormatEmptyRenderer() {
  return <Skeleton charCount={10} fillParent type="line" />;
}

export function emptyNumberFormatEmptyRenderer() {
  return '';
}

export function dashNumberFormatEmptyRenderer() {
  return '-';
}

export function doubleDashNumberFormatEmptyRenderer() {
  return '--';
}

function buildFloatingTypographyProps(base?: TypographyProps, override?: TypographyProps): TypographyProps {
  return {
    ...base,
    ...override,
    variant: override?.variant || base?.variant || 'mono',
    size: override?.size,
    lineHeight: override?.lineHeight,
    weight: override?.weight || base?.weight,
    tone: override?.tone || base?.tone,
  } as TypographyProps;
}

/**
 * style is omitted from Intl.NumberFormatOptions and renamed to formatStyle to prevent a clash with the
 * BaseHTMLAttributes "style" (which would be the CSS style object)
 */
export interface NumberFormatProps extends BaseHTMLAttributes<HTMLSpanElement>, Omit<Intl.NumberFormatOptions, 'style'> {
  floatingClassName?: string;
  floatingFractionDigits?: boolean;
  floatingOffset?: number;
  floatingPrefix?: boolean;
  floatingTypography?: TypographyProps;
  formatStyle?: keyof Intl.NumberFormatOptionsStyleRegistry;
  locale?: string;
  minusSignOverride?: string;
  plusSignOverride?: string;
  ref?: Ref<HTMLElement>;
  renderWhenEmpty?: (formattedParts: Intl.NumberFormatPart[]) => ReactNode;
  typography?: TypographyProps;
  value?: number | string | bigint;
  withoutTypography?: boolean;
}

export function NumberFormat({
  className,
  currency,
  currencySign,
  floatingFractionDigits,
  floatingClassName,
  floatingOffset,
  floatingPrefix,
  floatingTypography,
  formatStyle,
  locale = 'en-US',
  minimumIntegerDigits,
  minimumFractionDigits,
  maximumFractionDigits,
  minimumSignificantDigits,
  maximumSignificantDigits,
  minusSignOverride,
  plusSignOverride,
  renderWhenEmpty,
  signDisplay,
  useGrouping,
  typography,
  value,
  withoutTypography,
  ...rest
}: NumberFormatProps) {
  const usableTypographyProps = useMemo(() => ({ ...typography, variant: typography?.variant ?? 'mono' }) as TypographyProps, [typography]);
  const floatingTypographyProps = useMemo(
    () =>
      ({
        ...buildFloatingTypographyProps(usableTypographyProps, floatingTypography),
        style: floatingOffset ? { position: 'relative', top: floatingOffset, marginTop: -floatingOffset, display: 'inline-flex' } : undefined,
      }) satisfies TypographyProps & { style?: CSSProperties },
    [floatingTypography, usableTypographyProps, floatingOffset],
  );
  const [prefixPortion, wholePortion, fractionalPortion, backup] = useMemo(() => {
    try {
      const fractionalDigits = parseNumber(value?.toString() || '')?.split('.')[1]?.length;
      const baseFormatterOptions: Intl.NumberFormatOptions = {
        style: formatStyle,
        currency,
        currencySign,
        useGrouping,
        minimumIntegerDigits,
        minimumSignificantDigits,
        maximumSignificantDigits,
        signDisplay,
      };

      const baseFormatter =
        formatStyle === 'currency' ? getCurrencyFormatter(locale, baseFormatterOptions) : getIntlNumberFormat(locale, baseFormatterOptions);
      const resolvedMinFractionDigits = baseFormatter.resolvedOptions().minimumFractionDigits ?? 0;
      const resolvedMaxFractionDigits = baseFormatter.resolvedOptions().maximumFractionDigits ?? 0;

      baseFormatterOptions.minimumFractionDigits =
        minimumFractionDigits ?? (fractionalDigits !== undefined ? Math.max(fractionalDigits, resolvedMinFractionDigits) : undefined);
      baseFormatterOptions.maximumFractionDigits =
        maximumFractionDigits ??
        (fractionalDigits !== undefined ? Math.max(fractionalDigits, resolvedMinFractionDigits, resolvedMaxFractionDigits) : undefined);

      const numberFormatter =
        formatStyle === 'currency' ? getCurrencyFormatter(locale, baseFormatterOptions) : getIntlNumberFormat(locale, baseFormatterOptions);

      const parts = numberFormatter.formatToParts(value as number);

      const backupValue = renderWhenEmpty ? renderWhenEmpty(parts) : null;

      if (value === undefined || Number.isNaN(value)) {
        return ['', '', '', backupValue];
      }

      let prefix = '';
      let whole = '';
      let fractional = '';

      parts.forEach((part) => {
        if (['decimal', 'fraction', 'percentSign'].includes(part.type)) {
          fractional = `${fractional}${part.value}`;
        } else if (['plusSign', 'minusSign', 'currency'].includes(part.type)) {
          const signOverride = part.type === 'plusSign' ? plusSignOverride : part.type === 'minusSign' ? minusSignOverride : undefined;

          prefix = `${prefix}${signOverride || part.value}`;
        } else {
          whole = `${whole}${part.value}`;
        }
      });

      return [prefix, whole, fractional];
    } catch (err) {
      console.warn(`[FormatError]: ${value} could not be formatted.`, err);

      return ['', '', '', renderWhenEmpty ? renderWhenEmpty([]) : null];
    }
  }, [
    currency,
    currencySign,
    formatStyle,
    locale,
    maximumFractionDigits,
    maximumSignificantDigits,
    minimumFractionDigits,
    minimumIntegerDigits,
    minimumSignificantDigits,
    minusSignOverride,
    plusSignOverride,
    renderWhenEmpty,
    signDisplay,
    useGrouping,
    value,
  ]);

  const renderedPrefix = match({ wholePortion, fractionalPortion, floatingPrefix, prefixPortion })
    .with({ wholePortion: P.nullish, fractionalPortion: P.nullish }, () => null)
    .with({ floatingPrefix: true }, () =>
      withoutTypography ? (
        <sup className={floatingClassName}>{prefixPortion}</sup>
      ) : (
        <BaseTypography as="sup" className={floatingClassName} {...floatingTypographyProps}>
          {prefixPortion}
        </BaseTypography>
      ),
    )
    .otherwise(() => prefixPortion);
  const renderedWhole = match(wholePortion)
    .with(P.not(P.nullish), () => wholePortion)
    .otherwise(() => null);
  const renderedFractional = match({ fractionalPortion, floatingFractionDigits })
    .with({ fractionalPortion: P.not(P.nullish), floatingFractionDigits: true }, () =>
      withoutTypography ? (
        <sup className={floatingClassName}>{fractionalPortion}</sup>
      ) : (
        <BaseTypography as="sup" className={floatingClassName} inheritance="current" {...floatingTypographyProps}>
          {fractionalPortion}
        </BaseTypography>
      ),
    )
    .with({ fractionalPortion: P.not(P.nullish) }, () => fractionalPortion)
    .otherwise(() => null);

  const renderedNumber = match({ renderedPrefix, renderedWhole, renderedFractional })
    .with(P.union({ renderedWhole: P.when((val) => val) }, { renderedFractional: P.when((val) => val) }), () => (
      <>
        {renderedPrefix}
        {renderedWhole}
        {renderedFractional}
      </>
    ))
    .otherwise(() => backup);

  if (withoutTypography) {
    return <span data-number={value?.toString()}>{renderedNumber}</span>;
  }

  return (
    <BaseTypography className={className} data-number={value?.toString()} {...usableTypographyProps} {...rest}>
      {renderedNumber}
    </BaseTypography>
  );
}
